<template>
  <div class="pw-dash">
    <!-- ============ Header + filters ============ -->
    <div class="pw-dash__head">
      <div>
        <h1 class="pw-dash__title">{{ franchise ? "Franchise Dashboard" : "Admin Dashboard" }}</h1>
        <p class="pw-dash__sub">
          {{ franchise ? "Performance for" : "Franchise performance for" }}
          <strong>{{ franchise ? franchise.name : zoneLabel }}</strong> &middot;
          <strong>{{ data ? data.period.label : periodLabel }}</strong>
        </p>
      </div>

      <div class="pw-dash__filters">
        <v-select
          v-if="!franchise"
          v-model="filters.zone_id"
          :items="zoneItems"
          item-text="name"
          item-value="id"
          label="Zone"
          outlined
          dense
          hide-details
          :disabled="zoneLocked"
          class="pw-dash__filter pw-dash__filter--zone"
        />
        <v-select
          v-model="filters.month"
          :items="monthItems"
          label="Month"
          outlined
          dense
          hide-details
          class="pw-dash__filter"
          @change="onMonthChange"
        />
        <v-menu
          v-if="isCustom"
          v-model="rangeMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="rangeText"
              label="From - To"
              prepend-inner-icon="mdi-calendar-range"
              readonly
              outlined
              dense
              hide-details
              class="pw-dash__filter pw-dash__filter--range"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="range" range no-title scrollable :max="today">
            <v-spacer />
            <v-btn text @click="rangeMenu = false">Cancel</v-btn>
            <v-btn text color="primary" :disabled="range.length !== 2" @click="applyRange">Apply</v-btn>
          </v-date-picker>
        </v-menu>
        <v-select
          v-else
          v-model="filters.year"
          :items="yearItems"
          label="Year"
          outlined
          dense
          hide-details
          class="pw-dash__filter pw-dash__filter--year"
        />
        <v-btn text small color="primary" :disabled="isDefault" @click="resetFilters">
          <v-icon left size="16">mdi-refresh</v-icon>Reset
        </v-btn>
        <v-badge
          v-if="canAccess('action_dashboard')"
          :value="actions.toCall > 0"
          :content="actions.toCall"
          color="error"
          overlap
          offset-x="14"
          offset-y="12"
        >
          <v-btn depressed small color="warning" to="/action-dashboard" class="pw-dash__action-btn">
            <v-icon left size="18">mdi-lightning-bolt</v-icon>Action Dashboard
          </v-btn>
        </v-badge>
      </div>
    </div>

    <!-- Renewals that need a call -->
    <div v-if="canAccess('action_dashboard') && actions.toCall > 0" class="pw-action-strip">
      <v-icon color="warning darken-1">mdi-alert-outline</v-icon>
      <div class="pw-action-strip__text">
        <strong>{{ actions.toCall }} {{ actions.toCall === 1 ? "customer needs" : "customers need" }} a renewal call</strong>
        <span v-if="actions.expiring7"> &middot; {{ actions.expiring7 }} plan{{ actions.expiring7 === 1 ? "" : "s" }} expire in the next 7 days</span>
        <span v-if="actions.expired"> &middot; {{ actions.expired }} expired without renewal</span>
      </div>
      <v-btn text small color="primary" to="/action-dashboard">
        Open Action Dashboard<v-icon right small>mdi-arrow-right</v-icon>
      </v-btn>
    </div>

    <v-progress-linear
      :active="loading"
      indeterminate
      color="primary"
      height="2"
      class="pw-dash__progress"
    />

    <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>

    <div v-if="data" :class="{ 'is-loading': loading }" class="pw-dash__body">
      <!-- ============ KPI cards ============ -->
      <div class="pw-kpis">
        <div v-for="kpi in kpis" :key="kpi.key" class="pw-kpi">
          <div class="pw-kpi__icon" :class="`pw-tone--${kpi.tone}`">
            <v-icon size="22">{{ kpi.icon }}</v-icon>
          </div>
          <div class="pw-kpi__body">
            <div class="pw-kpi__label">{{ kpi.label }}</div>
            <div class="pw-kpi__value-row">
              <span class="pw-kpi__value">{{ kpi.value }}</span>
              <span
                v-if="kpi.delta"
                class="pw-delta"
                :class="`pw-delta--${kpi.delta.tone}`"
                :title="`Compared with ${data.period.previous_label}`"
              >
                <v-icon size="13">{{ kpi.delta.icon }}</v-icon>{{ kpi.delta.text }}
              </span>
            </div>
            <div v-if="kpi.sub" class="pw-kpi__sub">{{ kpi.sub }}</div>
            <!-- Parts that add up to the main number -->
            <div v-if="kpi.split" class="pw-kpi__split" :class="{ 'pw-kpi__split--stack': kpi.split.length > 2 }">
              <span v-for="s in kpi.split" :key="s.label">{{ s.label }} <strong>{{ s.value }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ Trends ============ -->
      <v-row dense class="pw-row">
        <v-col cols="12" lg="6">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Revenue &amp; Bookings Trend</h2>
              <span class="pw-card__meta">{{ data.period.year }}, month-wise</span>
            </header>
            <div class="pw-chart-label">
              <span class="pw-chart-label__key" :style="{ background: palette.blue }"></span>
              Revenue (₹)
            </div>
            <bar-chart
              :labels="trendLabels"
              :tooltip-labels="trendTooltipLabels"
              :series="[{ name: 'Revenue', color: palette.blue, values: trend.map((m) => m.revenue) }]"
              :highlight-index="highlightIndex"
              :format-tick="formatCompactCurrency"
              :format-value="formatCurrency"
              :height="170"
              aria-label="Revenue by month"
            />
            <div class="pw-chart-label mt-3">
              <span class="pw-chart-label__key" :style="{ background: palette.orange }"></span>
              Bookings
            </div>
            <bar-chart
              :labels="trendLabels"
              :tooltip-labels="trendTooltipLabels"
              :series="[{ name: 'Bookings', color: palette.orange, values: trend.map((m) => m.bookings) }]"
              :highlight-index="highlightIndex"
              :format-tick="formatCompactNumber"
              :format-value="formatNumber"
              :height="130"
              aria-label="Bookings by month"
            />
          </section>
        </v-col>

        <v-col cols="12" lg="6">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Customer Growth</h2>
              <div class="pw-legend">
                <span><i :style="{ background: palette.blue }"></i>New customers</span>
                <span><i :style="{ background: palette.orange }"></i>Active customers</span>
              </div>
            </header>
            <bar-chart
              :labels="trendLabels"
              :tooltip-labels="trendTooltipLabels"
              :series="[
                { name: 'New customers', color: palette.blue, values: trend.map((m) => m.new_customers) },
                { name: 'Active customers', color: palette.orange, values: trend.map((m) => m.active_customers) },
              ]"
              :highlight-index="highlightIndex"
              :format-tick="formatCompactNumber"
              :format-value="formatNumber"
              :height="336"
              aria-label="New and active customers by month"
            />
          </section>
        </v-col>
      </v-row>

      <!-- ============ Status, plans & leaders ============ -->
      <v-row dense class="pw-row">
        <v-col cols="12" md="6" :lg="franchise ? 6 : 4">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Walk Status</h2>
              <span class="pw-card__meta">{{ data.period.label }}</span>
            </header>
            <donut-chart
              :segments="walkStatusSegments"
              :center-value="formatNumber(data.kpis.walks.total)"
              center-label="Total walks"
              :format-value="formatNumber"
              aria-label="Walks by status"
            />
          </section>
        </v-col>

        <v-col cols="12" md="6" :lg="franchise ? 6 : 4">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Bookings by Plan</h2>
              <span class="pw-card__meta">{{ data.period.label }}</span>
            </header>
            <donut-chart
              :segments="planSegments"
              :center-value="formatNumber(totalBookings)"
              center-label="Bookings"
              :format-value="formatNumber"
              aria-label="Bookings by plan tier"
            />
          </section>
        </v-col>
        <v-col v-if="!franchise" cols="12" lg="4">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Top Performing Franchises</h2>
              <span class="pw-card__meta">{{ data.period.label }}</span>
            </header>
            <table class="pw-leaders">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Franchise</th>
                  <th class="text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in data.top_performers" :key="t.key">
                  <td>
                    <v-icon size="17" :color="leaderIcons[t.key].color" class="mr-2">
                      {{ leaderIcons[t.key].icon }}
                    </v-icon>
                    {{ t.metric }}
                  </td>
                  <td class="pw-leaders__name">{{ t.franchise || "—" }}</td>
                  <td class="text-right pw-num">{{ formatAs(t.format, t.value) }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </v-col>

      </v-row>

      <!-- ============ Revenue leaders & locations (comparing franchises: not for a franchise login) ============ -->
      <v-row v-if="!franchise" dense class="pw-row">
        <v-col cols="12" lg="5">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Top 5 Franchises by Revenue</h2>
              <span class="pw-card__meta">{{ data.period.label }}</span>
            </header>
            <ol v-if="topByRevenue.length" class="pw-toplist">
              <li v-for="(f, i) in topByRevenue" :key="f.id">
                <span class="pw-toplist__rank">{{ i + 1 }}</span>
                <span class="pw-toplist__name" :title="f.name">{{ f.name }}</span>
                <span class="pw-toplist__track">
                  <span
                    class="pw-toplist__bar"
                    :style="{ width: `${(f.revenue / topByRevenue[0].revenue) * 100}%`, background: palette.blue }"
                  ></span>
                </span>
                <span class="pw-toplist__value">{{ formatCurrency(f.revenue) }}</span>
              </li>
            </ol>
            <div v-else class="pw-empty">No revenue recorded for this period</div>
          </section>
        </v-col>

        <v-col cols="12" lg="7">
          <section class="pw-card">
            <header class="pw-card__head">
              <h2 class="pw-card__title">Franchise Locations</h2>
              <span class="pw-card__meta">
                {{ data.kpis.franchises.active }} active &middot; {{ data.kpis.franchises.inactive }} inactive
              </span>
            </header>
            <franchise-map :franchises="data.franchises" />
          </section>
        </v-col>
      </v-row>

      <!-- ============ Comparison & ranking ============ -->
      <v-row v-if="!franchise" dense class="pw-row">
        <v-col cols="12">
          <section class="pw-card">
            <header class="pw-card__head">
              <div>
                <h2 class="pw-card__title">Franchise Performance &amp; Ranking</h2>
                <div class="pw-card__note">
                  Ranked by performance score.
                  <v-tooltip bottom max-width="320">
                    <template v-slot:activator="{ on, attrs }">
                      <a v-bind="attrs" v-on="on" class="pw-link">How is it scored?</a>
                    </template>
                    <span>
                      Score out of 100: revenue 30%, completion rate 25%, low
                      cancellations 15%, customer rating 15%, customers 15%.
                      Revenue and customers are compared with the best franchise
                      in view. Franchises with no walks or revenue in the period
                      are not ranked.
                    </span>
                  </v-tooltip>
                </div>
              </div>
              <div class="pw-card__actions">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search franchise"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="pw-search"
                />
                <download-excel
                  :data="exportRows"
                  :fields="exportFields"
                  :name="`franchise-performance-${exportSuffix}.xls`"
                >
                  <v-btn outlined small color="primary" height="38">
                    <v-icon left size="17">mdi-download-outline</v-icon>Export
                  </v-btn>
                </download-excel>
              </div>
            </header>

            <v-data-table
              :headers="tableHeaders"
              :items="tableRows"
              :search="search"
              sort-by="rank_sort"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
              dense
              class="pw-table"
              no-data-text="No franchises in this zone"
            >
              <template v-slot:[`item.rank_sort`]="{ item }">
                <span v-if="item.rank" class="pw-rank" :class="{ 'pw-rank--top': item.rank <= 3 }">
                  {{ item.rank }}
                </span>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.name`]="{ item }">
                <div class="pw-fname">
                  <router-link :to="`/franchisees/${encryptIt(item.id)}`">{{ item.name }}</router-link>
                  <v-tooltip v-if="item.flags.length" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="pw-flag">
                        <v-icon size="13" color="#b42318">mdi-alert-circle-outline</v-icon>
                        Needs attention
                      </span>
                    </template>
                    <div v-for="f in item.flags" :key="f">{{ f }}</div>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.zone`]="{ item }">{{ titleCase(item.zone) || "—" }}</template>
              <template v-slot:[`item.customers`]="{ item }">{{ formatNumber(item.customers) }}</template>
              <template v-slot:[`item.total`]="{ item }">{{ formatNumber(item.total) }}</template>
              <template v-slot:[`item.completion_pct`]="{ item }">
                <span :class="{ 'pw-bad': item.completion_pct != null && item.completion_pct < thresholds.completion }">
                  {{ formatPercent(item.completion_pct) }}
                </span>
              </template>
              <template v-slot:[`item.cancellation_pct`]="{ item }">
                <span :class="{ 'pw-bad': item.cancellation_pct != null && item.cancellation_pct > thresholds.cancellation }">
                  {{ formatPercent(item.cancellation_pct) }}
                </span>
              </template>
              <template v-slot:[`item.revenue`]="{ item }">{{ formatCurrency(item.revenue) }}</template>
              <template v-slot:[`item.avg_rating`]="{ item }">
                <span v-if="item.avg_rating != null" :class="{ 'pw-bad': item.avg_rating < thresholds.rating }">
                  {{ formatRating(item.avg_rating) }}
                  <v-icon size="13" color="#eda100">mdi-star</v-icon>
                </span>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.score_sort`]="{ item }">
                <div v-if="item.score != null" class="pw-score">
                  <span class="pw-score__track">
                    <span class="pw-score__bar" :style="{ width: `${item.score}%`, background: palette.blue }"></span>
                  </span>
                  <span class="pw-num">{{ Math.round(item.score) }}</span>
                </div>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.is_active`]="{ item }">
                <span class="pw-status" :class="item.is_active ? 'pw-status--on' : 'pw-status--off'">
                  {{ item.is_active ? "Active" : "Inactive" }}
                </span>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <v-btn icon small :to="`/franchisees/${encryptIt(item.id)}`" title="View franchise">
                  <v-icon size="18">mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </section>
        </v-col>

      </v-row>

      <!-- ============ Walker performance (franchise login) ============ -->
      <v-row v-if="franchise" dense class="pw-row">
        <v-col cols="12">
          <section class="pw-card">
            <header class="pw-card__head">
              <div>
                <h2 class="pw-card__title">Active Walkers Performance</h2>
                <div class="pw-card__note">
                  Ranked by performance score.
                  <v-tooltip bottom max-width="320">
                    <template v-slot:activator="{ on, attrs }">
                      <a v-bind="attrs" v-on="on" class="pw-link">How is it scored?</a>
                    </template>
                    <span>
                      Score out of 100: completion rate 50%, customer rating 30%,
                      walks completed 20% (compared with your busiest walker).
                      Walks the customer cancelled are not counted against the
                      walker. Walkers with no walks due in the period are not ranked.
                    </span>
                  </v-tooltip>
                </div>
              </div>
              <div class="pw-card__actions">
                <v-text-field
                  v-model="walkerSearch"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search walker"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="pw-search"
                />
                <download-excel
                  :data="walkerExportRows"
                  :fields="walkerExportFields"
                  :name="`walker-performance-${exportSuffix}.xls`"
                >
                  <v-btn outlined small color="primary" height="38">
                    <v-icon left size="17">mdi-download-outline</v-icon>Export
                  </v-btn>
                </download-excel>
              </div>
            </header>

            <v-data-table
              :headers="walkerHeaders"
              :items="walkerRows"
              :search="walkerSearch"
              sort-by="rank_sort"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
              dense
              class="pw-table"
              no-data-text="No active walkers in your franchise"
            >
              <template v-slot:[`item.rank_sort`]="{ item }">
                <span v-if="item.rank" class="pw-rank" :class="{ 'pw-rank--top': item.rank <= 3 }">
                  {{ item.rank }}
                </span>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.name`]="{ item }">
                <div class="pw-fname">
                  <span>{{ item.name }}</span>
                  <v-tooltip v-if="item.flags.length" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="pw-flag">
                        <v-icon size="13" color="#b42318">mdi-alert-circle-outline</v-icon>
                        Needs attention
                      </span>
                    </template>
                    <div v-for="f in item.flags" :key="f">{{ f }}</div>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.employee_code`]="{ item }">
                <span v-if="item.employee_code" class="text-no-wrap">{{ String(item.employee_code).toUpperCase() }}</span>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.phone`]="{ item }">
                <a v-if="item.phone" :href="`tel:${item.phone}`" class="pw-link">{{ item.phone }}</a>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.customers`]="{ item }">{{ formatNumber(item.customers) }}</template>
              <template v-slot:[`item.total`]="{ item }">{{ formatNumber(item.total) }}</template>
              <template v-slot:[`item.completed`]="{ item }">{{ formatNumber(item.completed) }}</template>
              <template v-slot:[`item.missed`]="{ item }">
                <span :class="{ 'pw-bad': item.missed > 0 }">{{ formatNumber(item.missed) }}</span>
              </template>
              <template v-slot:[`item.upcoming`]="{ item }">{{ formatNumber(item.upcoming) }}</template>
              <template v-slot:[`item.completion_pct`]="{ item }">
                <span :class="{ 'pw-bad': item.completion_pct != null && item.completion_pct < thresholds.completion }">
                  {{ formatPercent(item.completion_pct) }}
                </span>
              </template>
              <template v-slot:[`item.avg_rating`]="{ item }">
                <span v-if="item.avg_rating != null" :class="{ 'pw-bad': item.avg_rating < thresholds.rating }">
                  {{ formatRating(item.avg_rating) }}
                  <v-icon size="13" color="#eda100">mdi-star</v-icon>
                </span>
                <span v-else class="pw-muted">—</span>
              </template>
              <template v-slot:[`item.score_sort`]="{ item }">
                <div v-if="item.score != null" class="pw-score">
                  <span class="pw-score__track">
                    <span class="pw-score__bar" :style="{ width: `${item.score}%`, background: palette.blue }"></span>
                  </span>
                  <span class="pw-num">{{ Math.round(item.score) }}</span>
                </div>
                <span v-else class="pw-muted">—</span>
              </template>
            </v-data-table>
          </section>
        </v-col>
      </v-row>
    </div>

    <div v-else-if="loading" class="pw-dash__loading">Loading dashboard…</div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import BarChart from "./BarChart.vue";
import DonutChart from "./DonutChart.vue";
import FranchiseMap from "./FranchiseMap.vue";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatRating,
  formatCompactCurrency,
  formatCompactNumber,
  formatters,
} from "./format";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Categorical slots, in fixed order (validated light-mode steps)
const PALETTE = {
  blue: "#2a78d6",
  orange: "#eb6834",
  aqua: "#1baf7a",
  yellow: "#eda100",
};

// Status colours: reserved for walk states, always shown with a label
const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
  neutral: "#c3c2b7",
};

// A franchise is flagged "Needs attention" when it crosses any of these
const THRESHOLDS = { completion: 85, cancellation: 10, rating: 4 };

// Plan tiers keep the same colour whatever the filter shows
const PLAN_COLORS = { Silver: PALETTE.blue, Gold: PALETTE.orange, Platinum: PALETTE.aqua };

export default {
  name: "AdminDashboard",
  components: { BarChart, DonutChart, FranchiseMap },
  data() {
    const today = new Date();
    return {
      // month is 1-12, null for the whole year, or "custom" for the from/to range
      defaults: { zone_id: null, month: today.getMonth() + 1, year: today.getFullYear(), from: null, to: null },
      filters: { zone_id: null, month: today.getMonth() + 1, year: today.getFullYear(), from: null, to: null },
      today: moment().format("YYYY-MM-DD"),
      rangeMenu: false,
      range: [],
      zones: [],
      // Next year down to 5 years back; the API widens it if bookings go further back
      years: Array.from({ length: 7 }, (_, i) => today.getFullYear() + 1 - i),
      zoneLocked: false,
      // A franchisee admin's own franchise ({ id, name }); null for admins and master franchisees
      franchise: null,
      data: null,
      loading: false,
      error: null,
      search: "",
      walkerSearch: "",
      requestId: 0,
      // Counts for the Action Dashboard button and strip
      actions: { toCall: 0, expiring7: 0, expired: 0 },
      palette: PALETTE,
      thresholds: THRESHOLDS,
      leaderIcons: {
        revenue: { icon: "mdi-trophy-outline", color: "#eda100" },
        completed: { icon: "mdi-check-circle-outline", color: "#0ca30c" },
        new_customers: { icon: "mdi-trending-up", color: "#2a78d6" },
        cancellation_pct: { icon: "mdi-percent-outline", color: "#d03b3b" },
        avg_rating: { icon: "mdi-star-outline", color: "#eda100" },
        walkers: { icon: "mdi-account-group-outline", color: "#4a3aa7" },
      },
    };
  },
  computed: {
    zoneItems() {
      const zones = this.zones.map((z) => ({ id: z.id, name: this.titleCase(z.name) }));
      return this.zoneLocked ? zones : [{ id: null, name: "All Zones" }, ...zones];
    },
    monthItems() {
      return [
        { text: "All months", value: null },
        ...MONTHS.map((m, i) => ({ text: m, value: i + 1 })),
        { text: "Custom", value: "custom" },
      ];
    },
    isCustom() {
      return this.filters.month === "custom";
    },
    rangeText() {
      const { from, to } = this.filters;
      if (!from || !to) return "";
      const fmt = (d) => moment(d).format("DD MMM YYYY");
      return from === to ? fmt(from) : `${fmt(from)} - ${fmt(to)}`;
    },
    yearItems() {
      return this.years;
    },
    zoneLabel() {
      const zone = this.zoneItems.find((z) => z.id === this.filters.zone_id);
      return zone ? zone.name : "All Zones";
    },
    periodLabel() {
      if (this.isCustom) return this.rangeText;
      return this.filters.month
        ? `${MONTHS[this.filters.month - 1]} ${this.filters.year}`
        : String(this.filters.year);
    },
    isDefault() {
      return (
        (this.zoneLocked || this.filters.zone_id === this.defaults.zone_id) &&
        this.filters.month === this.defaults.month &&
        this.filters.year === this.defaults.year &&
        this.filters.from === this.defaults.from &&
        this.filters.to === this.defaults.to
      );
    },
    trend() {
      return this.data ? this.data.monthly_trend : [];
    },
    trendLabels() {
      return this.trend.map((m) => m.label);
    },
    trendTooltipLabels() {
      return this.trend.map((m) => `${MONTHS[m.month - 1]} ${this.data.period.year}`);
    },
    highlightIndex() {
      return this.data && this.data.period.month ? this.data.period.month - 1 : null;
    },
    totalBookings() {
      return this.data.bookings_by_plan.reduce((sum, p) => sum + p.bookings, 0);
    },
    kpis() {
      const k = this.data.kpis;
      const prev = this.data.period.previous_label;
      const periodWord = this.data.period.custom
        ? "in this period"
        : this.data.period.month
        ? "this month"
        : "this year";

      const cards = [
        {
          key: "franchises",
          label: "Total Franchises",
          icon: "mdi-storefront-outline",
          tone: "blue",
          value: formatNumber(k.franchises.total),
          split: [
            { label: "Active", value: formatNumber(k.franchises.active) },
            { label: "Inactive", value: formatNumber(k.franchises.inactive) },
          ],
        },
        {
          key: "customers",
          label: "Total Customers",
          icon: "mdi-account-group-outline",
          tone: "green",
          value: formatNumber(k.customers.total),
          delta: this.delta(k.customers.change_pct, "%"),
          // New customers are already inside the total, so they are not a third part of it
          sub: `Includes ${formatNumber(k.customers.new)} new ${periodWord}`,
          split: [
            { label: "Active", value: formatNumber(k.customers.active) },
            { label: "Inactive", value: formatNumber(Math.max(0, k.customers.total - k.customers.active)) },
          ],
        },
        {
          key: "walks",
          label: "Total Walks",
          icon: "mdi-dog-side",
          tone: "violet",
          value: formatNumber(k.walks.total),
          delta: this.delta(k.walks.change_pct, "%"),
          sub: `From ${formatNumber(this.totalBookings)} ${this.totalBookings === 1 ? "booking" : "bookings"} ${periodWord}`,
          split: [
            { label: "Till today", value: formatNumber(k.walks.total - k.walks.upcoming) },
            { label: "Upcoming", value: formatNumber(k.walks.upcoming) },
          ],
        },
        {
          key: "completed",
          label: "Completed Walks",
          icon: "mdi-check-circle-outline",
          tone: "teal",
          value: formatNumber(k.completed.total),
          delta: this.delta(k.completed.change_pts, " pts"),
          // The rate is over walks that were due (dated before today, not rescheduled)
          sub: `${formatNumber(k.completed.total)} done out of ${formatNumber(k.walks.due)} walks till today (${formatPercent(k.completed.completion_pct)})`,
        },
        {
          key: "cancelled",
          label: "Cancelled / Rescheduled",
          icon: "mdi-calendar-remove-outline",
          tone: "red",
          value: `${formatNumber(k.cancelled_rescheduled.cancelled)} / ${formatNumber(k.cancelled_rescheduled.rescheduled)}`,
          // More cancellations is the bad direction
          delta: this.delta(k.cancelled_rescheduled.change_pts, " pts", true),
          sub: `${formatNumber(k.cancelled_rescheduled.cancelled)} cancelled out of ${formatNumber(k.walks.due)} walks till today (${formatPercent(k.cancelled_rescheduled.cancellation_pct)})`,
          // Who caused it: the customer, or the franchise side (walker on leave)
          split: [
            { label: "Cancelled by customer", value: formatNumber(k.cancelled_rescheduled.cancelled_by_customer ?? k.cancelled_rescheduled.cancelled) },
            { label: "Cancelled, walker on leave", value: formatNumber(k.cancelled_rescheduled.cancelled_by_walker_leave || 0) },
            { label: "Rescheduled by customer", value: formatNumber(k.cancelled_rescheduled.rescheduled_by_customer ?? k.cancelled_rescheduled.rescheduled) },
            { label: "Moved, walker on leave", value: formatNumber(k.cancelled_rescheduled.rescheduled_by_walker_leave || 0) },
          ],
        },
        {
          key: "revenue",
          label: "Total Revenue",
          icon: "mdi-currency-inr",
          tone: "orange",
          value: formatCurrency(k.revenue.total),
          delta: this.delta(k.revenue.change_pct, "%"),
          sub: `${formatCurrency(k.revenue.previous)} in ${prev}`,
        },
        {
          key: "rating",
          label: "Average Rating",
          icon: "mdi-star-outline",
          tone: "amber",
          value: formatRating(k.rating.average),
          delta: this.delta(k.rating.change, ""),
          sub: k.rating.count ? `Based on ${formatNumber(k.rating.count)} ratings` : "No ratings yet",
        },
      ];
      return this.franchise ? cards.filter((c) => c.key !== "franchises") : cards;
    },
    walkStatusSegments() {
      const s = this.data.walk_status;
      return [
        { label: "Completed", value: s.completed, color: STATUS.good },
        { label: "Cancelled", value: s.cancelled, color: STATUS.critical },
        { label: "Rescheduled", value: s.rescheduled, color: STATUS.warning },
        { label: "Missed", value: s.missed, color: STATUS.serious, hint: "Not completed on the walk date" },
        { label: "Upcoming", value: s.upcoming, color: STATUS.neutral, hint: "Scheduled, not due yet" },
      ];
    },
    planSegments() {
      const counts = {};
      this.data.bookings_by_plan.forEach((p) => (counts[p.plan] = p.bookings));
      const extra = Object.keys(counts).filter((p) => !PLAN_COLORS[p]);
      return [
        ...Object.keys(PLAN_COLORS).map((plan) => ({
          label: `${plan} Plan`,
          value: counts[plan] || 0,
          color: PLAN_COLORS[plan],
        })),
        ...extra.map((plan) => ({ label: plan, value: counts[plan], color: PALETTE.yellow })),
      ];
    },
    topByRevenue() {
      return [...this.data.franchises]
        .filter((f) => f.revenue > 0)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
    },
    tableHeaders() {
      return [
        { text: "Rank", value: "rank_sort", width: 64 },
        { text: "Franchise", value: "name" },
        { text: "Zone", value: "zone" },
        { text: "Customers", value: "customers", align: "end" },
        { text: "Walkers", value: "walkers", align: "end" },
        { text: "Total Walks", value: "total", align: "end" },
        { text: "Completed %", value: "completion_pct", align: "end" },
        { text: "Cancelled %", value: "cancellation_pct", align: "end" },
        { text: "Revenue", value: "revenue", align: "end" },
        { text: "Avg. Rating", value: "avg_rating", align: "end" },
        { text: "Score", value: "score_sort", width: 120 },
        { text: "Status", value: "is_active" },
        { text: "", value: "action", sortable: false, width: 48 },
      ];
    },
    tableRows() {
      return this.data.franchises.map((f) => ({
        ...f,
        rank_sort: f.rank == null ? 9999 : f.rank,
        score_sort: f.score == null ? -1 : f.score,
        flags: this.flagsFor(f),
      }));
    },
    exportFields() {
      return {
        Rank: "rank",
        Franchise: "name",
        Zone: "zone",
        Customers: "customers",
        "New Customers": "new_customers",
        Walkers: "walkers",
        "Total Walks": "total",
        Completed: "completed",
        "Completion %": "completion_pct",
        Cancelled: "cancelled",
        "Cancellation %": "cancellation_pct",
        Rescheduled: "rescheduled",
        Missed: "missed",
        "Revenue (INR)": "revenue",
        "Avg. Rating": "avg_rating",
        Score: "score",
        Status: "status",
      };
    },
    exportRows() {
      return [...this.tableRows]
        .sort((a, b) => a.rank_sort - b.rank_sort)
        .map((f) => ({ ...f, status: f.is_active ? "Active" : "Inactive" }));
    },
    walkerHeaders() {
      return [
        { text: "Rank", value: "rank_sort", width: 64 },
        { text: "Walker", value: "name" },
        { text: "Employee Code", value: "employee_code" },
        { text: "Phone", value: "phone" },
        { text: "Customers", value: "customers", align: "end" },
        { text: "Total Walks", value: "total", align: "end" },
        { text: "Completed", value: "completed", align: "end" },
        { text: "Missed", value: "missed", align: "end" },
        { text: "Upcoming", value: "upcoming", align: "end" },
        { text: "Completed %", value: "completion_pct", align: "end" },
        { text: "Avg. Rating", value: "avg_rating", align: "end" },
        { text: "Score", value: "score_sort", width: 120 },
      ];
    },
    walkerRows() {
      return (this.data.walkers || []).map((w) => ({
        ...w,
        rank_sort: w.rank == null ? 9999 : w.rank,
        score_sort: w.score == null ? -1 : w.score,
        flags: this.walkerFlagsFor(w),
      }));
    },
    walkerExportFields() {
      return {
        Rank: "rank",
        Walker: "name",
        "Employee Code": "employee_code",
        Phone: "phone",
        Customers: "customers",
        "Total Walks": "total",
        Completed: "completed",
        Missed: "missed",
        Upcoming: "upcoming",
        "Cancelled (walker on leave)": "cancelled_by_walker_leave",
        "Completion %": "completion_pct",
        "Avg. Rating": "avg_rating",
        Score: "score",
      };
    },
    walkerExportRows() {
      return [...this.walkerRows].sort((a, b) => a.rank_sort - b.rank_sort);
    },
    exportSuffix() {
      const p = this.data.period;
      if (p.custom) return `${p.from}-to-${p.to}`;
      return p.month ? `${p.year}-${String(p.month).padStart(2, "0")}` : p.year;
    },
  },
  watch: {
    rangeMenu(open) {
      if (open) this.range = [this.filters.from, this.filters.to].filter(Boolean);
    },
    filters: {
      deep: true,
      handler() {
        this.load();
      },
    },
  },
  mounted() {
    this.load();
    this.loadActions();
  },
  methods: {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatRating,
    formatCompactCurrency,
    formatCompactNumber,
    formatAs(format, value) {
      return (formatters[format] || formatNumber)(value);
    },
    async load() {
      const id = ++this.requestId;
      this.loading = true;
      this.error = null;
      try {
        const params = {};
        if (this.isCustom) {
          params.from = this.filters.from;
          params.to = this.filters.to;
        } else {
          params.year = this.filters.year;
          if (this.filters.month) params.month = this.filters.month;
        }
        if (this.filters.zone_id) params.zone_id = this.filters.zone_id;

        const { data } = await axios.get("admin_dashboard", { params });
        // A newer filter change has already been sent; drop this answer
        if (id !== this.requestId) return;

        this.zones = data.filters.zones;
        this.years = data.filters.years;
        this.zoneLocked = data.filters.zone_locked;
        this.franchise = data.filters.franchise || null;
        if (this.zoneLocked && this.filters.zone_id !== data.filters.zone_id) {
          this.defaults.zone_id = data.filters.zone_id;
          this.filters.zone_id = data.filters.zone_id; // re-fetches, same zone
        }
        this.data = data.data;
      } catch (e) {
        if (id !== this.requestId) return;
        const body = e.response && e.response.data;
        this.error = (body && body.message) || "Could not load the dashboard. Please try again.";
      } finally {
        if (id === this.requestId) this.loading = false;
      }
    },
    onMonthChange(month) {
      if (month === "custom") {
        // Start from this month so far; the picker opens to change it
        this.filters = {
          ...this.filters,
          from: this.filters.from || moment().startOf("month").format("YYYY-MM-DD"),
          to: this.filters.to || this.today,
        };
        this.$nextTick(() => (this.rangeMenu = true));
      } else if (this.filters.from || this.filters.to) {
        this.filters = { ...this.filters, from: null, to: null };
      }
    },
    applyRange() {
      const [from, to] = [...this.range].sort();
      this.rangeMenu = false;
      this.filters = { ...this.filters, from, to: to || from };
    },
    async loadActions() {
      if (!this.canAccess("action_dashboard")) return;
      try {
        const { data } = await axios.get("reports/action_dashboard", { params: { summary: 1 } });
        const first = (key, i = 0) => {
          const tile = data.data.tiles.find((t) => t.key === key);
          return tile && tile.summary[i] ? Number(tile.summary[i].value) || 0 : 0;
        };
        this.actions = {
          toCall: first("action_follow_up"),
          expiring7: first("action_expiring_7", 1),
          expired: first("action_expired_not_renewed"),
        };
      } catch (e) {
        // The badge is a hint; the dashboard works without it
      }
    },
    resetFilters() {
      this.filters = { ...this.defaults };
    },
    delta(value, unit, upIsBad = false) {
      if (value == null) return null;
      const up = value > 0;
      const flat = value === 0;
      return {
        icon: flat ? "mdi-minus" : up ? "mdi-arrow-up" : "mdi-arrow-down",
        text: `${Math.abs(value)}${unit}`,
        tone: flat ? "flat" : up !== upIsBad ? "good" : "bad",
      };
    },
    flagsFor(f) {
      const flags = [];
      if (f.completion_pct != null && f.completion_pct < THRESHOLDS.completion) {
        flags.push(`Completion ${formatPercent(f.completion_pct)} (target ${THRESHOLDS.completion}%+)`);
      }
      if (f.cancellation_pct != null && f.cancellation_pct > THRESHOLDS.cancellation) {
        flags.push(`Cancellations ${formatPercent(f.cancellation_pct)} (limit ${THRESHOLDS.cancellation}%)`);
      }
      if (f.avg_rating != null && f.avg_rating < THRESHOLDS.rating) {
        flags.push(`Rating ${formatRating(f.avg_rating)} (target ${THRESHOLDS.rating}+)`);
      }
      if (f.is_active && f.due === 0 && f.revenue === 0) {
        flags.push("No walks or revenue in this period");
      }
      return flags;
    },
    walkerFlagsFor(w) {
      const flags = [];
      if (w.completion_pct != null && w.completion_pct < THRESHOLDS.completion) {
        flags.push(`Completion ${formatPercent(w.completion_pct)} (target ${THRESHOLDS.completion}%+)`);
      }
      if (w.missed > 0) {
        flags.push(`${w.missed} missed ${w.missed === 1 ? "walk" : "walks"}`);
      }
      if (w.avg_rating != null && w.avg_rating < THRESHOLDS.rating) {
        flags.push(`Rating ${formatRating(w.avg_rating)} (target ${THRESHOLDS.rating}+)`);
      }
      return flags;
    },
    titleCase(text) {
      if (!text) return text;
      return text.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    },
  },
};
</script>

<style scoped>
.pw-dash__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.pw-dash__title {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1a2436;
  line-height: 1.25;
}

.pw-dash__sub {
  font-size: 0.875rem;
  color: #66738a;
  margin: 4px 0 0;
}

.pw-dash__sub strong {
  color: #1a2436;
  font-weight: 600;
}

.pw-dash__filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pw-dash__filter {
  width: 160px;
  flex: 0 0 auto;
  background: #fff;
}

.pw-dash__filter--zone {
  width: 190px;
}

.pw-dash__action-btn {
  font-weight: 600;
}

.pw-action-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff8e6;
  border: 1px solid #f6dfa0;
  border-radius: 12px;
  padding: 8px 14px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #5c4a12;
}

.pw-action-strip__text {
  flex: 1 1 auto;
}

.pw-dash__filter--year {
  width: 110px;
}

.pw-dash__filter--range {
  width: 240px;
}

.pw-dash__progress {
  margin-bottom: 12px;
  border-radius: 2px;
}

.pw-dash__body {
  transition: opacity 0.2s ease;
}

.pw-dash__body.is-loading {
  opacity: 0.6;
}

.pw-dash__loading {
  padding: 60px 0;
  text-align: center;
  color: #8d99ad;
}

/* ---------- KPI cards ---------- */
.pw-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(215px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.pw-kpi {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 14px;
}

.pw-kpi__icon {
  flex: 0 0 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pw-kpi__body {
  min-width: 0;
}

.pw-kpi__label {
  font-size: 0.8125rem;
  color: #66738a;
  font-weight: 500;
}

.pw-kpi__value-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin: 2px 0;
}

.pw-kpi__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a2436;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.pw-kpi__sub {
  font-size: 0.75rem;
  color: #8d99ad;
  line-height: 1.35;
}

.pw-kpi__split {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 8px;
  padding-top: 7px;
  border-top: 1px solid #eef1f5;
  font-size: 0.75rem;
  color: #66738a;
}

/* Longer breakdowns: one line each, figure on the right */
.pw-kpi__split--stack {
  flex-direction: column;
  gap: 2px;
}

.pw-kpi__split--stack span {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.pw-kpi__split strong {
  color: #1a2436;
  margin-left: 3px;
  font-variant-numeric: tabular-nums;
}

.pw-tone--blue { background: #eaf2fc; }
.pw-tone--blue .v-icon { color: #2a78d6; }
.pw-tone--green { background: #e7f6ee; }
.pw-tone--green .v-icon { color: #16834e; }
.pw-tone--violet { background: #efedfb; }
.pw-tone--violet .v-icon { color: #4a3aa7; }
.pw-tone--teal { background: #e5f6f1; }
.pw-tone--teal .v-icon { color: #12805a; }
.pw-tone--red { background: #fcebeb; }
.pw-tone--red .v-icon { color: #c03030; }
.pw-tone--orange { background: #fdf0e9; }
.pw-tone--orange .v-icon { color: #c9531f; }
.pw-tone--amber { background: #fdf5e1; }
.pw-tone--amber .v-icon { color: #b07800; }

.pw-delta {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.pw-delta .v-icon {
  color: inherit;
}

.pw-delta--good { color: #006300; }
.pw-delta--bad { color: #b42318; }
.pw-delta--flat { color: #8d99ad; }

/* ---------- Cards ---------- */
.pw-row {
  margin: 0 -6px 0;
}

.pw-row > .col {
  padding: 6px;
}

.pw-card {
  height: 100%;
  padding: 16px 18px 18px;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 14px;
}

.pw-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.pw-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a2436;
  line-height: 1.3;
}

.pw-card__meta {
  font-size: 0.75rem;
  color: #8d99ad;
  padding-top: 2px;
}

.pw-card__note {
  font-size: 0.75rem;
  color: #8d99ad;
  margin-top: 2px;
}

.pw-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pw-link {
  color: var(--v-primary-base);
  text-decoration: underline dotted;
  cursor: help;
}

.pw-search {
  width: 220px;
}

.pw-chart-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #52514e;
  margin-bottom: 4px;
}

.pw-chart-label__key,
.pw-legend i {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.pw-legend {
  display: flex;
  gap: 14px;
  font-size: 0.75rem;
  color: #52514e;
  padding-top: 3px;
}

.pw-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pw-empty {
  padding: 48px 0;
  text-align: center;
  font-size: 0.8125rem;
  color: #8d99ad;
}

/* ---------- Top 5 list ---------- */
.pw-toplist {
  list-style: none;
  padding: 0;
  margin: 0;
}

.pw-toplist li {
  display: grid;
  grid-template-columns: 24px minmax(80px, 130px) 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  font-size: 0.8125rem;
  color: #1a2436;
}

.pw-toplist__rank {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f2f5f9;
  color: #66738a;
  font-size: 0.6875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pw-toplist__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pw-toplist__track {
  height: 14px;
}

.pw-toplist__bar {
  display: block;
  height: 100%;
  min-width: 3px;
  border-radius: 0 4px 4px 0;
}

.pw-toplist__value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

/* ---------- Leaders ---------- */
.pw-leaders {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  color: #1a2436;
}

.pw-leaders th {
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8d99ad;
  padding: 0 6px 8px;
  border-bottom: 1px solid #eef1f5;
}

.pw-leaders td {
  padding: 8px 6px;
  border-bottom: 1px solid #f2f4f7;
}

.pw-leaders tr:last-child td {
  border-bottom: 0;
}

.pw-leaders__name {
  font-weight: 500;
}

.pw-num {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* ---------- Table ---------- */
::v-deep .pw-table th {
  white-space: nowrap;
  font-size: 0.75rem !important;
}

::v-deep .pw-table td {
  font-size: 0.8125rem !important;
  font-variant-numeric: tabular-nums;
  height: 44px !important;
  white-space: nowrap;
}

/* Numbers line up on the right, under their headers */
::v-deep .pw-table .text-end {
  text-align: right !important;
}

.pw-fname a {
  color: #1a2436;
  font-weight: 600;
  text-decoration: none;
}

.pw-fname a:hover {
  color: var(--v-primary-base);
}

.pw-flag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fef3f2;
  color: #b42318;
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: default;
}

.pw-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f2f5f9;
  color: #46536b;
  font-size: 0.75rem;
  font-weight: 700;
}

.pw-rank--top {
  background: #eaf2fc;
  color: #1c5cab;
}

.pw-bad {
  color: #b42318;
  font-weight: 600;
}

.pw-muted {
  color: #b6bfcc;
}

.pw-score {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pw-score__track {
  flex: 1 1 auto;
  height: 6px;
  min-width: 50px;
  border-radius: 3px;
  background: #eef1f5;
  overflow: hidden;
}

.pw-score__bar {
  display: block;
  height: 100%;
  border-radius: 3px;
}

.pw-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pw-status--on {
  background: #e7f6ee;
  color: #006300;
}

.pw-status--off {
  background: #fef3f2;
  color: #b42318;
}

@media (max-width: 600px) {
  .pw-dash__filter,
  .pw-dash__filter--zone,
  .pw-dash__filter--year,
  .pw-dash__filter--range,
  .pw-search {
    width: 100%;
  }

  .pw-card__actions {
    width: 100%;
  }
}
</style>
